import { Controller } from "@hotwired/stimulus"

//Complementa a los banners nativos: Smart App Banner
// de Safari (meta apple-itunes-app) y el banner de Chrome hacia Play (manifest de
// consumidor). Ver _docs/mobile/android/app_links_setup.md
export default class extends Controller {
  static values = {
    appUrl: String,          // App Store
    playUrl: String,         // Google Play
    androidPackage: String,  // package de la app Android (intent + getInstalledRelatedApps)
    autoOpen: { type: Boolean, default: false }, // Rails: true solo en páginas de consumidor (/stores)
    delayMs: { type: Number, default: 1000 }
  }

  connect() {
    const inStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone
    if (inStandalone) return

    this.platform = this._platform()
    if (!this.platform) return

    // Verificar si el usuario ya cerró el banner en esta sesión
    const KEY_DISMISSED_SESSION = "gf_app_banner_dismissed_session"
    if (sessionStorage.getItem(KEY_DISMISSED_SESSION)) {
      return
    }

    // Verificar si el banner ya existe (para evitar duplicados)
    if (document.getElementById("gf-app-banner-modal")) {
      return
    }

    // Android con la app instalada: abrirla en vez de ofrecer descargarla.
    this._openInstalledAppOnce().then((opened) => {
      if (opened) return
      this._scheduleModal()
    })
  }

  _scheduleModal() {
    // Mostrar el banner después del delay, pero solo la primera vez
    const KEY_SESSION = "gf_app_banner_shown_session"
    if (!sessionStorage.getItem(KEY_SESSION)) {
      sessionStorage.setItem(KEY_SESSION, "true")
      setTimeout(() => this._showModal(), this.delayMsValue)
    } else {
      // Si ya se mostró antes en esta sesión, mostrarlo inmediatamente
      this._showModal()
    }
  }

  // Android: si la app de Play está instalada, navega al intent:// de la ruta actual (sin
  // fallback: no queremos mandar a Play sin un gesto del usuario). Solo en las páginas que
  // Rails marca como de consumidor (autoOpen: /stores y sus páginas; nunca el landing ni
  // rutas de partner) y una vez por sesión, para que quien vuelva a Chrome a propósito pueda
  // seguir navegando la web. En el resto de páginas el sheet ofrece "Abrir en la app".
  // Resuelve true si se disparó la apertura.
  async _openInstalledAppOnce() {
    if (this.platform !== "android") return false
    if (!this.autoOpenValue) return false
    if (!("getInstalledRelatedApps" in navigator)) return false
    const KEY_REDIRECTED_SESSION = "gf_native_app_opened_session"
    if (sessionStorage.getItem(KEY_REDIRECTED_SESSION)) return false

    const installed = await this._installedApp()
    if (!installed) return false

    sessionStorage.setItem(KEY_REDIRECTED_SESSION, "true")
    window.location.href = this._androidIntentUrl({ fallback: false })
    return true
  }

  async _installedApp() {
    try {
      const apps = await navigator.getInstalledRelatedApps()
      return apps.some((app) => app.platform === "play" && app.id === this.androidPackageValue)
    } catch {
      return false
    }
  }

  _platform() {
    const ua = navigator.userAgent || navigator.vendor || window.opera
    if (/iPhone|iPad|iPod/i.test(ua) && !window.MSStream) return "ios"
    if (/android/i.test(ua)) return "android"
    return null
  }

  _showModal() {
    // Verificar si el banner ya existe
    if (document.getElementById("gf-app-banner-modal")) {
      return
    }

    const android = this.platform === "android"
    const storeLabel = android ? "Descargar en Google Play" : "Descargar en App Store"
    const storeIcon = android
      ? `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
           <path d="M3.6 2.2c-.4.2-.6.6-.6 1.1v17.4c0 .5.2.9.6 1.1l9.7-9.8-9.7-9.8zm11 8.5L5.3 2.4 16 8.5l-1.4 2.2zM5.3 21.6l9.3-8.3 1.4 2.2-10.7 6.1zm11.6-7.1 3.3-1.9c.8-.5.8-1.7 0-2.2l-3.3-1.9L14.9 12l2 2.5z"/>
         </svg>`
      : `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
           <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
         </svg>`

    // crea modal simple en tiempo real
    const modal = document.createElement("div")
    modal.id = "gf-app-banner-modal"
    modal.innerHTML = `
      <div style="
        position:fixed;bottom:0;left:0;right:0;z-index:9999;
        background:#fff;
        padding:16px 20px;
        box-shadow:0 -4px 20px rgba(0,0,0,0.15);
        font-family:-apple-system, BlinkMacSystemFont, Roboto, sans-serif;
        border-top-left-radius:16px;border-top-right-radius:16px;
        border-top:1px solid #e5e7eb;">

        <div style="display:flex;align-items:center;justify-content:space-between;">
          <div style="display:flex;align-items:center;">
            <div style="
              width:48px;height:48px;border-radius:10px;
              background:#fff;
              display:flex;align-items:center;justify-content:center;
              margin-right:12px;
              box-shadow:0 2px 8px rgba(0,0,0,0.1);">
              <img src="/images/app-icon.png" alt="Greatfood" style="width:40px;height:40px;border-radius:8px;">
            </div>
            <div>
              <div style="font-size:16px;font-weight:600;color:#111827;margin-bottom:2px;">
                Greatfood
              </div>
              <div id="gf-app-banner-text" style="font-size:13px;color:#6b7280;">
                Para una mejor experiencia descarga la aplicación.
              </div>

            </div>
          </div>
          <button id="gf-close-banner" style="
            padding:8px;
            background:none;border:none;
            color:#9ca3af;font-size:18px;
            cursor:pointer;">
            ✕
          </button>
        </div>

        <div style="margin-top:12px;">
          <button id="gf-open-appstore" style="
            width:100%;
            background:#000;color:#fff;border:none;
            padding:12px;border-radius:8px;
            font-size:15px;font-weight:500;
            cursor:pointer;
            display:flex;align-items:center;justify-content:center;
            gap:8px;">
            ${storeIcon}
            <span id="gf-open-appstore-label">${storeLabel}</span>
          </button>
        </div>
      </div>`
    document.body.appendChild(modal)

    document.getElementById("gf-open-appstore").onclick = () => this._openStore(modal)
    document.getElementById("gf-close-banner").onclick = () => this._dismissBanner(modal)

    if (android) this._labelIfInstalled()
  }

  // Android: si la app está instalada pero no se abrió sola (landing, ya abierta en esta
  // sesión), el botón pasa a "Abrir en la app". Si la API no existe o falla, queda el label
  // de descarga.
  _labelIfInstalled() {
    if (!("getInstalledRelatedApps" in navigator)) return
    this._installedApp().then((installed) => {
      if (!installed) return
      const label = document.getElementById("gf-open-appstore-label")
      const text = document.getElementById("gf-app-banner-text")
      if (label) label.textContent = "Abrir en la app"
      if (text) text.textContent = "Ya tienes la app instalada: ábrela para una mejor experiencia."
    })
  }

  _openStore(modal) {
    if (this.platform === "android") {
      // intent:// abre la app instalada; si no está, Chrome sigue al fallback (Play).
      // Tiene que ser navegación top-level tras un gesto del usuario: no window.open.
      window.location.href = this._androidIntentUrl()
    } else {
      const url = this.appUrlValue || "https://apps.apple.com/cl/app/greatfoods/id6753152334"
      // abrir en nueva pestaña evita redirect total en Safari
      window.open(url, "_blank", "noopener")
    }

    // Marcar como cerrado en esta sesión
    sessionStorage.setItem("gf_app_banner_dismissed_session", "true")

    // Remover el modal
    modal.remove()
  }

  _androidIntentUrl({ fallback = true } = {}) {
    const { host, pathname, search } = window.location
    const pkg = this.androidPackageValue ? `package=${this.androidPackageValue};` : ""
    const playUrl = this.playUrlValue || "https://play.google.com/store/apps/details?id=io.gfood.greatfood"
    const fallbackParam = fallback ? `S.browser_fallback_url=${encodeURIComponent(playUrl)};` : ""
    return `intent://${host}${pathname}${search}#Intent;scheme=https;${pkg}${fallbackParam}end`
  }

  _dismissBanner(modal) {
    // Marcar como cerrado en esta sesión
    sessionStorage.setItem("gf_app_banner_dismissed_session", "true")

    // Remover el modal
    modal.remove()
  }
}
